import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import api, { authAPI, messagesAPI, withdrawalsAPI } from '../services/api';
import { getSocket, onNewMessage, onMessagesRead, offEvent } from '../services/socket';
import { usdt } from '../utils/currency';
import { renderRichText } from '../utils/richText';
import './Profile.css';

// ── Helpers messagerie (regroupement type iMessage/Telegram) ──
const isSameDay = (a, b) => {
  const d1 = new Date(a), d2 = new Date(b);
  return d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate();
};

const chatDayLabel = (dateStr, lang) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(d, today)) return lang === 'en' ? 'Today' : "Aujourd'hui";
  if (isSameDay(d, yesterday)) return lang === 'en' ? 'Yesterday' : 'Hier';
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {})
  });
};

const chatTimeLabel = (dateStr, lang) =>
  new Date(dateStr).toLocaleTimeString(lang === 'en' ? 'en-US' : 'fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });

// Deux messages du même expéditeur à moins de 5 min sont "groupés"
const areGrouped = (a, b) => {
  if (!a || !b) return false;
  if (a.senderRole !== b.senderRole) return false;
  if (!isSameDay(a.createdAt, b.createdAt)) return false;
  return Math.abs(new Date(b.createdAt) - new Date(a.createdAt)) < 5 * 60 * 1000;
};

const Profile = () => {
  const { user } = useAuth();
  const { language, setLanguage, t, availableLanguages } = useLanguage();
  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section') || null;
  const [activeSection, setActiveSection] = useState(sectionFromUrl);
  const [referralStats, setReferralStats] = useState(null);
  const [appInfo, setAppInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedHelp, setExpandedHelp] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(['root']);

  // États pour les paramètres
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // États KYC
  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycDocType, setKycDocType] = useState('CNI');

  // État bonus parrainage
  const [investingBonus, setInvestingBonus] = useState(false);
  const [bonusMessage, setBonusMessage] = useState('');
  const [kycDocNumber, setKycDocNumber] = useState('');
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [kycError, setKycError] = useState('');
  const [kycSuccess, setKycSuccess] = useState('');

  // États messagerie
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [remainingMessages, setRemainingMessages] = useState(3);
  const [unreadCount, setUnreadCount] = useState(0);
  const chatContainerRef = useRef(null); // scroll auto vers les derniers messages

  // Synchroniser activeSection avec l'URL quand elle change
  useEffect(() => {
    setActiveSection(sectionFromUrl);
  }, [sectionFromUrl]);

  // Navigation avec scroll to top
  const goToSection = (section) => {
    setActiveSection(section);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const toggleHelp = (id) => {
    setExpandedHelp(expandedHelp === id ? null : id);
  };

  // Gérer le changement de langue
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };

  // Gérer le changement de mot de passe
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError(t('settings.passwordMismatch'));
      return;
    }
    if (passwordForm.new.length < 6) {
      setPasswordError(t('settings.passwordTooShort'));
      return;
    }

    try {
      await authAPI.updateProfile({
        currentPassword: passwordForm.current,
        newPassword: passwordForm.new
      });
      setPasswordSuccess(t('settings.passwordChanged'));
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.error || t('common.error'));
    }
  };

  // Se recharge quand le rang ou le nombre de filleuls change dans le contexte Auth
  useEffect(() => {
    fetchReferralStats();
    fetchAppInfo();
    fetchWithdrawInfo();
  }, [user?.rank, user?.referralCount, user?.referralEarnings]);

  const fetchAppInfo = async () => {
    try {
      const response = await authAPI.getAppInfo();
      setAppInfo(response.data);
    } catch (error) {
      console.error('Erreur chargement infos app:', error);
    }
  };

  // Valeurs de retrait (frais %, nb/mois) lues dynamiquement pour le guide
  const [withdrawInfo, setWithdrawInfo] = useState({ feePercent: 5, perMonth: 1 });
  const fetchWithdrawInfo = async () => {
    try {
      const res = await withdrawalsAPI.getStatus();
      setWithdrawInfo({
        feePercent: res.data.feePercent ?? 5,
        perMonth: res.data.withdrawalsPerMonth ?? 1
      });
    } catch (e) { /* garde les valeurs par défaut */ }
  };

  // Mettre à jour la section quand l'URL change
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['overview', 'referral', 'kyc', 'help', 'settings', 'support'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Charger les messages quand la section support est ouverte
  useEffect(() => {
    if (activeSection === 'support') {
      fetchMessages();
    }
  }, [activeSection]);

  // Toujours scroller la conversation jusqu'aux DERNIERS messages
  // (à l'ouverture de la section et à chaque nouveau message)
  useEffect(() => {
    if (activeSection === 'support' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [activeSection, messages, messagesLoading]);

  // Charger le compteur de messages non lus
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const res = await messagesAPI.getUnreadCount();
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {
        console.error('Erreur compteur messages:', err);
      }
    };
    loadUnread();
  }, []);

  // Écouter les nouveaux messages en temps réel via Socket.io
  useEffect(() => {
    const handleNewMessage = (data) => {
      // Vérifier que le message est pour cet utilisateur
      if (data.userId === user?.id) {
        // Ajouter le message à la liste
        setMessages(prev => [...prev, data.message]);
        // Mettre à jour readAt des messages utilisateur si fourni (admin a lu)
        if (data.readAt) {
          setMessages(prev => prev.map(m =>
            m.senderRole === 'user' && !m.readAt
              ? { ...m, readAt: data.readAt }
              : m
          ));
          // L'admin a répondu donc tous les messages sont "répondus" → reset le compteur
          setRemainingMessages(3);
        }
        // Si on n'est pas sur la section support, incrémenter le badge
        if (activeSection !== 'support') {
          setUnreadCount(prev => prev + 1);
        }
      }
    };

    onNewMessage(handleNewMessage);

    // Écouter quand l'admin a lu les messages (confirmation de lecture temps réel)
    const handleMessagesRead = (data) => {
      if (data.userId === user?.id) {
        setMessages(prev => prev.map(m =>
          m.senderRole === 'user' && !m.readAt
            ? { ...m, readAt: data.readAt }
            : m
        ));
      }
    };

    onMessagesRead(handleMessagesRead);

    return () => {
      offEvent('new_message_for_user');
      offEvent('messages_read');
    };
  }, [user?.id, activeSection]);

  const fetchMessages = async () => {
    try {
      setMessagesLoading(true);
      setMessageError('');
      const res = await messagesAPI.getAll();
      setMessages(res.data.messages || []);
      setRemainingMessages(res.data.remainingMessages || 0);
      setUnreadCount(0);
    } catch (err) {
      setMessageError(t('support.loadError'));
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || messageSending) return;

    try {
      setMessageSending(true);
      setMessageError('');
      const res = await messagesAPI.send(messageInput.trim());
      // Ajouter le message directement à la liste (temps réel)
      if (res.data.message) {
        setMessages(prev => [...prev, {
          ...res.data.message,
          senderRole: 'user',
          senderName: user?.username
        }]);
      }
      setMessageInput('');
      setRemainingMessages(res.data.remainingMessages || 0);
    } catch (err) {
      setMessageError(err.response?.data?.error || t('support.sendError'));
    } finally {
      setMessageSending(false);
    }
  };

  // Charger le statut KYC quand la section kyc est ouverte
  useEffect(() => {
    if (activeSection === 'kyc') {
      fetchKYCStatus();
    }
  }, [activeSection]);

  const fetchReferralStats = async () => {
    try {
      const response = await api.get('/auth/referral-stats');
      setReferralStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      setReferralStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchKYCStatus = async () => {
    try {
      setKycLoading(true);
      setKycError('');
      const res = await authAPI.getKYCStatus();
      setKycData(res.data);
    } catch (err) {
      setKycError(t('kyc.loadError'));
    } finally {
      setKycLoading(false);
    }
  };

  // Validation client du numéro de document
  const validateDocNumber = (type, number) => {
    if (type === 'CNI') {
      if (!/^\d{15}$/.test(number)) {
        return t('kyc.cniFormatError');
      }
    } else if (type === 'PASSPORT') {
      if (!/^[A-Z]\d{8,9}$/.test(number)) {
        return t('kyc.passportFormatError');
      }
    }
    return null;
  };

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    setKycError('');
    setKycSuccess('');

    const docNumber = kycDocNumber.toUpperCase().trim();
    const validationError = validateDocNumber(kycDocType, docNumber);
    if (validationError) {
      setKycError(validationError);
      return;
    }

    try {
      setKycSubmitting(true);
      await authAPI.submitKYC(kycDocType, docNumber);
      setKycSuccess(t('kyc.submitSuccess'));
      setKycDocNumber('');
      await fetchKYCStatus();
    } catch (err) {
      setKycError(err.response?.data?.error || t('kyc.submitError'));
    } finally {
      setKycSubmitting(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referralCode) {
      navigator.clipboard.writeText(referralStats.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Investir le bonus parrainage en attente
  const handleInvestBonus = async () => {
    setInvestingBonus(true);
    setBonusMessage('');
    try {
      const response = await authAPI.investReferralBonus();
      setBonusMessage(`${response.data.message}`);
      // Recharger les stats
      const statsRes = await api.get('/auth/referral-stats');
      setReferralStats(statsRes.data);
      // Recharger le profil utilisateur
      window.location.reload();
    } catch (error) {
      setBonusMessage(error.response?.data?.error || t('profile.investError'));
    } finally {
      setInvestingBonus(false);
    }
  };

  const getRankIcon = (rank) => {
    const icons = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇',
      platinum: '💎',
      diamond: '👑'
    };
    return icons[rank] || '🥉';
  };

  const getRankColor = (rank) => {
    const colors = {
      bronze: '#cd7f32',
      silver: '#c0c0c0',
      gold: '#ffd700',
      platinum: '#e5e4e2',
      diamond: '#b9f2ff'
    };
    return colors[rank] || '#cd7f32';
  };

  const formatCurrency = (value) => usdt(value, 0);

  // Données des rangs — seuils depuis le serveur (via referral-stats), fallback local
  const ranksData = referralStats?.ranks || [
    { name: 'bronze', icon: '🥉', required: 0, color: '#cd7f32' },
    { name: 'silver', icon: '🥈', required: 2, color: '#c0c0c0' },
    { name: 'gold', icon: '🥇', required: 5, color: '#ffd700' },
    { name: 'platinum', icon: '💎', required: 10, color: '#e5e4e2' },
    { name: 'diamond', icon: '👑', required: 20, color: '#b9f2ff' }
  ];

  // Calculer l'index du rang actuel et la progression
  const currentRankIndex = ranksData.findIndex(r => r.name === (referralStats?.rank || 'bronze'));
  const currentRank = ranksData[currentRankIndex] || ranksData[0];
  const nextRank = ranksData[currentRankIndex + 1];
  const referralCount = referralStats?.referralCount || 0;

  // Calculer le pourcentage de progression vers le prochain rang
  const getProgressToNextRank = () => {
    if (!nextRank) return 100; // Déjà au max
    const currentRequired = currentRank.required;
    const nextRequired = nextRank.required;
    const progress = ((referralCount - currentRequired) / (nextRequired - currentRequired)) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  return (
    <Layout>
      <div className="profile-page">
        {/* Header et Stats - masqués quand une section est active */}
        {activeSection === null && (
        <>
        <div className="profile-header-enhanced">
          <div className="profile-top-section">
            <div className="profile-avatar-large">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <h1>{user?.username}</h1>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>

          {/* Rank Progress Section - Compact */}
          <div className="rank-progress-section">
            <div className="ranks-progress-bar">
              {ranksData.map((rank, index) => {
                const isUnlocked = index <= currentRankIndex;
                const isCurrent = index === currentRankIndex;
                return (
                  <div
                    key={rank.name}
                    className={`rank-node ${isUnlocked ? 'unlocked' : ''} ${isCurrent ? 'current' : ''}`}
                  >
                    <div
                      className="rank-node-icon"
                      style={{
                        borderColor: isUnlocked ? rank.color : '#3c4451',
                        background: isUnlocked ? `${rank.color}20` : '#1e2329'
                      }}
                    >
                      <span style={{ filter: isUnlocked ? 'none' : 'grayscale(1) opacity(0.4)' }}>
                        {rank.icon}
                      </span>
                    </div>
                    {index < ranksData.length - 1 && (
                      <div className={`rank-connector ${index < currentRankIndex ? 'filled' : ''}`}>
                        {index === currentRankIndex && (
                          <div
                            className="rank-connector-progress"
                            style={{ width: `${getProgressToNextRank()}%` }}
                          ></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {nextRank ? (
              <div className="next-rank-progress-info">
                <div className="progress-text">
                  <span>{referralCount}/{nextRank.required}</span>
                  <span className="next-rank-name">→ {nextRank.icon}</span>
                </div>
                <div className="mini-progress-bar">
                  <div className="mini-progress-fill" style={{ width: `${getProgressToNextRank()}%` }}></div>
                </div>
              </div>
            ) : (
              <div className="max-rank-badge">
                <span>👑 Max</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="profile-stats-grid">
          <div className="profile-stat-card">
            <span className="stat-icon">👥</span>
            <div className="stat-info">
              <span className="stat-number">{referralStats?.referralCount || 0}</span>
              <span className="stat-label">{t('profile.referrals')}</span>
            </div>
          </div>
          <div className="profile-stat-card">
            <span className="stat-icon">💰</span>
            <div className="stat-info">
              <span className="stat-number">{formatCurrency(referralStats?.referralEarnings)}</span>
              <span className="stat-label">{t('profile.bonusEarned')}</span>
            </div>
          </div>
          <div className="profile-stat-card">
            <span className="stat-icon">🌳</span>
            <div className="stat-info">
              <span className="stat-number">{referralStats?.totalDownlineCount || 0}</span>
              <span className="stat-label">{t('profile.totalDownline')}</span>
            </div>
          </div>
        </div>
        </>
        )}

        {/* Menu Sections - masqué quand une section est active */}
        {activeSection === null && (
          <div className="profile-menu-list">
            <button
              className="profile-menu-item"
              onClick={() => goToSection('overview')}
            >
              <span className="menu-icon">👤</span>
              <span className="menu-text">{t('profile.information')}</span>
              <span className="menu-arrow">›</span>
            </button>
            <button
              className="profile-menu-item"
              onClick={() => goToSection('referral')}
            >
              <span className="menu-icon">🎁</span>
              <span className="menu-text">{t('profile.referral')}</span>
              <span className="menu-arrow">›</span>
            </button>
            <button
              className="profile-menu-item"
              onClick={() => goToSection('kyc')}
            >
              <span className="menu-icon">🪪</span>
              <span className="menu-text">Vérification d'identité</span>
              <span className="menu-arrow">
                {user?.kycStatus === 'verified'   && <span className="kyc-menu-badge verified">Vérifié</span>}
                {user?.kycStatus === 'pending'    && <span className="kyc-menu-badge pending">En attente</span>}
                {user?.kycStatus === 'rejected'   && <span className="kyc-menu-badge rejected">Rejeté</span>}
                {(!user?.kycStatus || user?.kycStatus === 'unverified') && <span className="kyc-menu-badge unverified">Non vérifié</span>}
              </span>
            </button>
            <button
              className="profile-menu-item"
              onClick={() => goToSection('help')}
            >
              <span className="menu-icon">❓</span>
              <span className="menu-text">{t('profile.helpContact')}</span>
              <span className="menu-arrow">›</span>
            </button>
            <button
              className="profile-menu-item"
              onClick={() => goToSection('support')}
            >
              <span className="menu-icon">💬</span>
              <span className="menu-text">Support</span>
              <span className="menu-arrow">
                {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                ›
              </span>
            </button>
            <button
              className="profile-menu-item"
              onClick={() => goToSection('settings')}
            >
              <span className="menu-icon">⚙️</span>
              <span className="menu-text">{t('profile.settings')}</span>
              <span className="menu-arrow">›</span>
            </button>
          </div>
        )}

        {/* Content Section - avec navigation */}
        {activeSection !== null && (
          <div className="profile-content">
            {/* Navigation header */}
            <div className="section-nav">
              <button className="nav-back" onClick={() => goToSection(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              <h2 className="section-title">
                {activeSection === 'overview' && t('profile.information')}
                {activeSection === 'referral' && t('profile.referral')}
                {activeSection === 'kyc' && t('profile.kycTitle')}
                {activeSection === 'help' && t('profile.helpContact')}
                {activeSection === 'support' && 'Support'}
                {activeSection === 'settings' && t('profile.settings')}
              </h2>
            </div>

          {activeSection === 'overview' && (
            <div className="section-card no-header">
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">{t('profile.userId')}</span>
                  <span className="info-value user-id">
                    {user?.id?.slice(0, 8).toUpperCase() || '--------'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('auth.username')}</span>
                  <span className="info-value">{user?.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('auth.email')}</span>
                  <span className="info-value">{user?.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('profile.memberSince')}</span>
                  <span className="info-value">
                    {new Date(user?.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('profile.currentRank')}</span>
                  <span className="info-value" style={{ color: getRankColor(referralStats?.rank) }}>
                    {getRankIcon(referralStats?.rank)} {referralStats?.rank?.toUpperCase()}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('profile.status')}</span>
                  <span className="info-value status-active">● {t('common.active')}</span>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'referral' && (
            <div className="referral-section">
              {/* Bonus en attente d'investissement */}
              {user?.pendingReferralBonus > 0 && (
                <div className="section-card pending-bonus-card" style={{
                  background: user?.capitalConfirmed
                    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: '#fff',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>🎁 {t('profile.pendingBonus')}: {(parseFloat(user.pendingReferralBonus) || 0).toFixed(2)} USDT</h3>

                  {user?.capitalConfirmed ? (
                    <>
                      <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Investissez ce bonus pour l'ajouter à votre capital de trading.
                      </p>
                      <button
                        onClick={handleInvestBonus}
                        disabled={investingBonus}
                        style={{
                          background: '#000',
                          color: '#fbbf24',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          border: 'none',
                          fontWeight: 'bold',
                          cursor: investingBonus ? 'not-allowed' : 'pointer',
                          width: '100%'
                        }}
                      >
                        {investingBonus ? t('profile.investing') : t('profile.investBonusCta').replace('{amount}', (parseFloat(user.pendingReferralBonus) || 0).toFixed(2))}
                      </button>
                    </>
                  ) : (
                    <p style={{ fontSize: '0.9rem', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                      ⚠️ Pour investir ce bonus, vous devez d'abord effectuer votre propre dépôt et confirmer votre capital.
                      Le bonus de parrainage ne peut pas être le premier argent sur votre compte.
                    </p>
                  )}

                  {bonusMessage && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', textAlign: 'center', color: '#fff' }}>
                      {bonusMessage}
                    </p>
                  )}
                </div>
              )}

              {/* Code de parrainage */}
              <div className="section-card referral-code-card">
                <h3>{t('profile.yourReferralCode')}</h3>
                <p className="referral-description">
                  {t('profile.shareCodeDescription')}
                </p>
                <div className="referral-code-box">
                  <span className="referral-code">{referralStats?.referralCode || '--------'}</span>
                  <button className="copy-btn" onClick={copyReferralCode}>
                    {copied ? `✓ ${t('common.copied')}` : t('common.copy')}
                  </button>
                </div>
              </div>

              {/* Comment ça marche */}
              <div className="section-card">
                <h3>{t('profile.howItWorks')}</h3>
                <div className="steps-list">
                  <div className="step-item">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>{t('profile.step1Title')}</h4>
                      <p>{t('profile.step1Desc')}</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>{t('profile.step2Title')}</h4>
                      <p>{t('profile.step2Desc')}</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>{t('profile.step3Title')}</h4>
                      <p>{t('profile.step3Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hiérarchie des rangs - Arbre unique */}
              <div className="section-card">
                <h3>⚡ {t('profile.bonusSignalsTitle')}</h3>
                <div className="bonus-signals-box" style={{ padding: '4px 2px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#f0b90b' }}>
                      {referralStats?.baseSignals ?? 3}
                      {referralStats?.activeReferralCodes > 0 && (
                        <span style={{ color: '#22c55e' }}> +{referralStats.activeReferralCodes}</span>
                      )}
                    </span>
                    <span style={{ opacity: 0.7 }}>{t('profile.signalsPerDay')}</span>
                  </div>
                  <p style={{ fontSize: 13, opacity: 0.7, margin: '6px 0 10px' }}>
                    {t('profile.bonusSignalsInfo')
                      .replace('{days}', referralStats?.codeValidityDays ?? 30)
                      .replace('{cap}', referralStats?.bonusSignalsCap ?? 10)}
                  </p>
                  {referralStats?.activeBonusCodes?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {referralStats.activeBonusCodes.map((c, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 8px', background: 'rgba(34,197,94,0.06)', borderRadius: 6 }}>
                          <span>⚡ {c.username}</span>
                          <span style={{ color: c.daysLeft <= 5 ? '#ef4444' : '#848e9c' }}>
                            {t('profile.expiresIn').replace('{days}', c.daysLeft)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: 13, opacity: 0.6, fontStyle: 'italic' }}>{t('profile.noBonusSignals')}</p>
                  )}
                </div>
              </div>

              <div className="section-card">
                <h3>{t('profile.rankHierarchy')}</h3>
                <div className="rank-tree-container">
                  {/* Arbre unique : Diamond (sommet) → Bronze (feuilles) */}
                  <svg className="rank-tree-svg" viewBox="0 0 300 380" preserveAspectRatio="xMidYMid meet">
                    {/* Lignes de connexion verticales */}
                    <g className="tree-connections">
                      {/* Diamond → Platinum */}
                      <line x1="150" y1="56" x2="150" y2="80" stroke={currentRankIndex >= 4 ? '#b9f2ff' : '#3c4451'} strokeWidth="2"/>
                      {/* Platinum → Gold (branches) */}
                      <line x1="150" y1="125" x2="100" y2="155" stroke={currentRankIndex >= 3 ? '#e5e4e2' : '#3c4451'} strokeWidth="2"/>
                      <line x1="150" y1="125" x2="200" y2="155" stroke={currentRankIndex >= 3 ? '#e5e4e2' : '#3c4451'} strokeWidth="2"/>
                      {/* Gold → Silver (branches multiples) */}
                      <line x1="100" y1="195" x2="50" y2="230" stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      <line x1="100" y1="195" x2="110" y2="230" stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      <line x1="200" y1="195" x2="190" y2="230" stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      <line x1="200" y1="195" x2="250" y2="230" stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      {/* Silver → Bronze (feuilles) */}
                      <line x1="50" y1="260" x2="30" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <line x1="50" y1="260" x2="70" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <line x1="110" y1="260" x2="110" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <line x1="190" y1="260" x2="190" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <line x1="250" y1="260" x2="230" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <line x1="250" y1="260" x2="270" y2="295" stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                    </g>

                    {/* Niveau 1 - Diamond (racine/sommet) - 20+ filleuls */}
                    <g className={`rank-node-svg ${referralStats?.rank === 'diamond' ? 'current' : ''} ${currentRankIndex >= 4 ? 'unlocked' : ''}`}>
                      <circle cx="150" cy="30" r="26" fill={currentRankIndex >= 4 ? '#b9f2ff20' : '#1e2329'} stroke={currentRankIndex >= 4 ? '#b9f2ff' : '#3c4451'} strokeWidth="3"/>
                      <text x="150" y="37" textAnchor="middle" fontSize="20">👑</text>
                      <text x="150" y="68" textAnchor="middle" fill={currentRankIndex >= 4 ? '#b9f2ff' : '#848e9c'} fontSize="10" fontWeight="600">DIAMOND · 20+</text>
                    </g>

                    {/* Niveau 2 - Platinum - 10+ filleuls */}
                    <g className={`rank-node-svg ${referralStats?.rank === 'platinum' ? 'current' : ''} ${currentRankIndex >= 3 ? 'unlocked' : ''}`}>
                      <circle cx="150" cy="105" r="22" fill={currentRankIndex >= 3 ? '#e5e4e220' : '#1e2329'} stroke={currentRankIndex >= 3 ? '#e5e4e2' : '#3c4451'} strokeWidth="2"/>
                      <text x="150" y="112" textAnchor="middle" fontSize="18">💎</text>
                      <text x="150" y="138" textAnchor="middle" fill={currentRankIndex >= 3 ? '#e5e4e2' : '#848e9c'} fontSize="10" fontWeight="500">PLATINUM · 10+</text>
                    </g>

                    {/* Niveau 3 - Gold (x2) - 5+ filleuls */}
                    <g className={`rank-node-svg ${referralStats?.rank === 'gold' ? 'current' : ''} ${currentRankIndex >= 2 ? 'unlocked' : ''}`}>
                      <circle cx="100" cy="175" r="18" fill={currentRankIndex >= 2 ? '#ffd70020' : '#1e2329'} stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      <text x="100" y="181" textAnchor="middle" fontSize="15">🥇</text>
                      <circle cx="200" cy="175" r="18" fill={currentRankIndex >= 2 ? '#ffd70020' : '#1e2329'} stroke={currentRankIndex >= 2 ? '#ffd700' : '#3c4451'} strokeWidth="2"/>
                      <text x="200" y="181" textAnchor="middle" fontSize="15">🥇</text>
                      <text x="150" y="205" textAnchor="middle" fill={currentRankIndex >= 2 ? '#ffd700' : '#848e9c'} fontSize="10" fontWeight="500">GOLD · 5+</text>
                    </g>

                    {/* Niveau 4 - Silver (x4) - 2+ filleuls */}
                    <g className={`rank-node-svg ${referralStats?.rank === 'silver' ? 'current' : ''} ${currentRankIndex >= 1 ? 'unlocked' : ''}`}>
                      <circle cx="50" cy="245" r="15" fill={currentRankIndex >= 1 ? '#c0c0c020' : '#1e2329'} stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <text x="50" y="250" textAnchor="middle" fontSize="12">🥈</text>
                      <circle cx="110" cy="245" r="15" fill={currentRankIndex >= 1 ? '#c0c0c020' : '#1e2329'} stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <text x="110" y="250" textAnchor="middle" fontSize="12">🥈</text>
                      <circle cx="190" cy="245" r="15" fill={currentRankIndex >= 1 ? '#c0c0c020' : '#1e2329'} stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <text x="190" y="250" textAnchor="middle" fontSize="12">🥈</text>
                      <circle cx="250" cy="245" r="15" fill={currentRankIndex >= 1 ? '#c0c0c020' : '#1e2329'} stroke={currentRankIndex >= 1 ? '#c0c0c0' : '#3c4451'} strokeWidth="2"/>
                      <text x="250" y="250" textAnchor="middle" fontSize="12">🥈</text>
                      <text x="150" y="275" textAnchor="middle" fill={currentRankIndex >= 1 ? '#c0c0c0' : '#848e9c'} fontSize="10" fontWeight="500">SILVER · 2+</text>
                    </g>

                    {/* Niveau 5 - Bronze (feuilles) - 0+ filleuls */}
                    <g className={`rank-node-svg ${referralStats?.rank === 'bronze' ? 'current' : ''} unlocked`}>
                      <circle cx="30" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="30" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <circle cx="70" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="70" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <circle cx="110" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="110" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <circle cx="190" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="190" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <circle cx="230" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="230" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <circle cx="270" cy="315" r="12" fill="#cd7f3220" stroke="#cd7f32" strokeWidth="2"/>
                      <text x="270" y="319" textAnchor="middle" fontSize="10">🥉</text>
                      <text x="150" y="345" textAnchor="middle" fill="#cd7f32" fontSize="10" fontWeight="500">BRONZE · 0+</text>
                    </g>

                    {/* Indicateur du rang actuel */}
                    <text x="150" y="370" textAnchor="middle" fill="#f0b90b" fontSize="11" fontWeight="600">
                      {t('profile.you')}: {referralStats?.rank?.toUpperCase() || 'BRONZE'} ({referralCount} {t('profile.referrals')})
                    </text>
                  </svg>

                  {/* Légende des avantages */}
                  <div className="rank-benefits-legend">
                    <div className={`benefit-item ${referralStats?.rank === 'bronze' ? 'current' : ''}`}>
                      <span className="benefit-rank">🥉</span>
                      <span className="benefit-text">{t('help.bronzeBenefit')}</span>
                    </div>
                    <div className={`benefit-item ${referralStats?.rank === 'silver' ? 'current' : ''}`}>
                      <span className="benefit-rank">🥈</span>
                      <span className="benefit-text">{t('help.silverBenefit')}</span>
                    </div>
                    <div className={`benefit-item ${referralStats?.rank === 'gold' ? 'current' : ''}`}>
                      <span className="benefit-rank">🥇</span>
                      <span className="benefit-text">{t('help.goldBenefit')}</span>
                    </div>
                    <div className={`benefit-item ${referralStats?.rank === 'platinum' ? 'current' : ''}`}>
                      <span className="benefit-rank">💎</span>
                      <span className="benefit-text">{t('help.platinumBenefit')}</span>
                    </div>
                    <div className={`benefit-item ${referralStats?.rank === 'diamond' ? 'current' : ''}`}>
                      <span className="benefit-rank">👑</span>
                      <span className="benefit-text">{t('help.diamondBenefit')}</span>
                    </div>
                  </div>
                </div>

                {referralStats?.nextRank && (
                  <div className="next-rank-info">
                    <p>
                      {t('help.moreReferralsToReach')} <strong>{referralStats.nextRank.remaining}</strong> {t('help.referralsWord')} {t('help.toReachRank')} <strong>{referralStats.nextRank.name.toUpperCase()}</strong>
                    </p>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${((referralStats.referralCount / referralStats.nextRank.required) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Mes filleuls - Vue Arbre uniquement */}
              {referralStats?.recentReferrals?.length > 0 && (
                <div className="section-card">
                  <h3>{t('profile.myReferrals')}</h3>
                  <div className="referral-tree">
                    {/* Noeud racine (l'utilisateur actuel) */}
                    <div className="tree-node root">
                      <div
                        className="node-content root-node"
                        onClick={() => setExpandedNodes(prev =>
                          prev.includes('root')
                            ? prev.filter(n => n !== 'root')
                            : [...prev, 'root']
                        )}
                      >
                        <div className="node-avatar root-avatar">
                          {user?.username?.charAt(0).toUpperCase()}
                        </div>
                        <div className="node-info">
                          <span className="node-name">{user?.username}</span>
                          <span className="node-status you">{t('profile.you')}</span>
                        </div>
                        <div className="node-stats">
                          <span className="node-rank">{getRankIcon(referralStats?.rank)}</span>
                          <span className="node-count">{referralStats?.referralCount} {t('profile.referrals')}</span>
                        </div>
                        <span className={`expand-icon ${expandedNodes.includes('root') ? 'expanded' : ''}`}>
                          {referralStats?.recentReferrals?.length > 0 ? '▼' : ''}
                        </span>
                      </div>

                      {/* Branches vers les filleuls */}
                      {expandedNodes.includes('root') && referralStats?.recentReferrals?.length > 0 && (
                        <div className="tree-children">
                          <div className="tree-line"></div>
                          {referralStats.recentReferrals.map((ref, index) => (
                            <div key={index} className="tree-node child">
                              <div className="tree-branch"></div>
                              <div className="node-content">
                                <div className="node-avatar">
                                  {ref.username.charAt(0).toUpperCase()}
                                </div>
                                <div className="node-info">
                                  <span className="node-name">{ref.username}</span>
                                  <span className="node-date">
                                    {new Date(ref.joinedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                                  </span>
                                </div>
                                <div className="node-badge">
                                  <span className="node-status active">{t('common.active')}</span>
                                  <span className="node-bonus">+20%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════
               KYC — VÉRIFICATION D'IDENTITÉ
               ══════════════════════════════ */}
          {activeSection === 'kyc' && (
            <div className="kyc-profile-section">
              <h3 className="kyc-title">{t('kyc.title')}</h3>
              <p className="kyc-subtitle">
                {t('kyc.subtitle1')}<br/>
                {t('kyc.subtitle2')}
              </p>

              {kycLoading ? (
                <div className="kyc-loading">{t('kyc.loading')}</div>
              ) : (
                <>
                  {/* ── Statut actuel ── */}
                  {kycData && (
                    <div className={`kyc-status-card ${kycData.kycStatus}`}>
                      {kycData.kycStatus === 'unverified' && (
                        <>
                          <span className="kyc-status-icon">👤</span>
                          <div className="kyc-status-body">
                            <span className="kyc-status-label">{t('kyc.statusUnverified')}</span>
                            <span className="kyc-status-desc">{t('kyc.statusUnverifiedDesc')}</span>
                          </div>
                        </>
                      )}
                      {kycData.kycStatus === 'pending' && (
                        <>
                          <span className="kyc-status-icon">⏳</span>
                          <div className="kyc-status-body">
                            <span className="kyc-status-label">{t('kyc.statusPending')}</span>
                            <span className="kyc-status-desc">
                              {t('kyc.document')} : <strong>{kycData.documentType}</strong><br/>
                              {t('kyc.submittedOn')} : {new Date(kycData.submittedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
                                day: '2-digit', month: 'long', year: 'numeric'
                              })}
                            </span>
                          </div>
                        </>
                      )}
                      {kycData.kycStatus === 'verified' && (
                        <>
                          <span className="kyc-status-icon">✅</span>
                          <div className="kyc-status-body">
                            <span className="kyc-status-label">{t('kyc.statusVerified')}</span>
                            <span className="kyc-status-desc">
                              {t('kyc.verifiedOn')} {new Date(kycData.verifiedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
                                day: '2-digit', month: 'long', year: 'numeric'
                              })}
                            </span>
                          </div>
                        </>
                      )}
                      {kycData.kycStatus === 'rejected' && (
                        <>
                          <span className="kyc-status-icon">❌</span>
                          <div className="kyc-status-body">
                            <span className="kyc-status-label">{t('kyc.statusRejected')}</span>
                            {kycData.rejectedReason && (
                              <span className="kyc-status-desc">{t('kyc.reason')} : {kycData.rejectedReason}</span>
                            )}
                            <span className="kyc-status-desc">{t('kyc.resubmitHint')}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ── Formulaire de soumission (unverified ou rejected) ── */}
                  {kycData && ['unverified', 'rejected'].includes(kycData.kycStatus) && (
                    <form className="kyc-form" onSubmit={handleKYCSubmit} noValidate>
                      <h4 className="kyc-form-title">{t('kyc.formTitle')}</h4>

                      {kycError   && <div className="kyc-form-error">{kycError}</div>}
                      {kycSuccess && <div className="kyc-form-success">{kycSuccess}</div>}

                      {/* Type de document */}
                      <div className="kyc-field">
                        <label className="kyc-label">{t('kyc.docTypeLabel')}</label>
                        <div className="kyc-type-btns">
                          {['CNI', 'PASSPORT'].map(type => (
                            <button
                              key={type}
                              type="button"
                              className={`kyc-type-btn ${kycDocType === type ? 'active' : ''}`}
                              onClick={() => { setKycDocType(type); setKycDocNumber(''); setKycError(''); }}
                            >
                              {type === 'CNI' ? `🪪 ${t('kyc.cniLabel')}` : `📕 ${t('kyc.passportLabel')}`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Numéro de document */}
                      <div className="kyc-field">
                        <label className="kyc-label" htmlFor="kycDocNumber">
                          {t('kyc.docNumberLabel')}
                        </label>
                        <input
                          id="kycDocNumber"
                          type="text"
                          className="kyc-input"
                          value={kycDocNumber}
                          onChange={e => {
                            const val = kycDocType === 'CNI'
                              ? e.target.value.replace(/\D/g, '').slice(0, 15)
                              : e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                            setKycDocNumber(val);
                            setKycError('');
                          }}
                          placeholder={kycDocType === 'CNI'
                            ? t('kyc.cniPlaceholder')
                            : t('kyc.passportPlaceholder')}
                          maxLength={kycDocType === 'CNI' ? 15 : 10}
                          autoComplete="off"
                        />
                        <span className="kyc-format-hint">
                          {kycDocType === 'CNI'
                            ? t('kyc.cniCounter').replace('{n}', kycDocNumber.length)
                            : t('kyc.passportCounter').replace('{n}', kycDocNumber.length)}
                        </span>
                      </div>

                      <button
                        type="submit"
                        className="kyc-submit-btn"
                        disabled={kycSubmitting || (
                          kycDocType === 'CNI'
                            ? kycDocNumber.length !== 15
                            : kycDocNumber.length < 7
                        )}
                      >
                        {kycSubmitting ? t('profile.kycSending') : t('profile.kycSubmitButton')}
                      </button>
                    </form>
                  )}

                  {/* ── En attente: pas de formulaire ── */}
                  {kycData?.kycStatus === 'pending' && (
                    <div className="kyc-pending-info">
                      {t('kyc.pendingInfo')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeSection === 'help' && (
            <div className="help-section">

              {/* ── GUIDE UTILISATEUR ── */}
              <div className="section-card">
                <h3>{t('guide.title')}</h3>
                <p className="guide-subtitle">{t('guide.subtitle')}</p>
                <div className="accordion-list">

                  {/* PREMIERS PAS */}
                  <div className={`accordion-item ${expandedHelp === 'start' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('start')}>
                      <span className="accordion-icon">🚀</span>
                      <span className="accordion-title">{t('guide.gettingStarted')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'start' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'start' && (
                      <div className="accordion-content guide-steps">
                        <h4>{t('guide.startTitle')}</h4>
                        <div className="guide-step">
                          <strong>{t('guide.startStep1Title')}</strong>
                          <p>{t('guide.startStep1')}</p>
                        </div>
                        <div className="guide-step">
                          <strong>{t('guide.startStep2Title')}</strong>
                          <p>{t('guide.startStep2')}</p>
                        </div>
                        <div className="guide-step">
                          <strong>{t('guide.startStep3Title')}</strong>
                          <p>{t('guide.startStep3')}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RECHARGER SON COMPTE */}
                  <div className={`accordion-item ${expandedHelp === 'deposit' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('deposit')}>
                      <span className="accordion-icon">💳</span>
                      <span className="accordion-title">{t('guide.depositFunds')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'deposit' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'deposit' && (
                      <div className="accordion-content guide-steps">
                        <h4>{t('guide.depositTitle')}</h4>
                        <div className="guide-step"><p>{t('guide.depositStep1')}</p></div>
                        <div className="guide-step"><p>{t('guide.depositStep2')}</p></div>
                        <div className="guide-step"><p>{t('guide.depositStep3')}</p></div>
                        <div className="guide-step"><p>{t('guide.depositStep4')}</p></div>
                        <div className="guide-note">{t('guide.depositNote')}</div>
                      </div>
                    )}
                  </div>

                  {/* TRADING */}
                  <div className={`accordion-item ${expandedHelp === 'trading' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('trading')}>
                      <span className="accordion-icon">📈</span>
                      <span className="accordion-title">{t('guide.tradingGuide')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'trading' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'trading' && (
                      <div className="accordion-content guide-steps">
                        <h4>{t('guide.tradeTitle')}</h4>
                        <div className="guide-step"><p>{t('guide.tradeStep1')}</p></div>
                        <div className="guide-step"><p>{t('guide.tradeStep2')}</p></div>
                        <div className="guide-step"><p>{t('guide.tradeStep3')}</p></div>
                        <div className="guide-step"><p>{t('guide.tradeStep4')}</p></div>
                        <div className="guide-step"><p>{t('guide.tradeStep5')}</p></div>
                        <div className="guide-step"><p>{t('guide.tradeStep6')}</p></div>
                        <div className="guide-alternatives">
                          <p><strong>✕</strong> {t('guide.tradeReject')}</p>
                          <p><strong>⏰</strong> {t('guide.tradeMissed')}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RETRAIT */}
                  <div className={`accordion-item ${expandedHelp === 'withdraw' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('withdraw')}>
                      <span className="accordion-icon">💰</span>
                      <span className="accordion-title">{t('guide.withdrawFunds')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'withdraw' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'withdraw' && (
                      <div className="accordion-content guide-steps">
                        <h4>{t('guide.withdrawTitle')}</h4>
                        <div className="guide-step"><p>{t('guide.withdrawStep1')}</p></div>
                        <div className="guide-step"><p>{t('guide.withdrawStep2')}</p></div>
                        <div className="guide-step"><p>{t('guide.withdrawStep3')}</p></div>
                        <div className="guide-step"><p>{t('guide.withdrawStep4')}</p></div>
                        <div className="guide-step"><p>{t('guide.withdrawStep5')}</p></div>
                        <div className="guide-step"><p>{t('guide.withdrawStep6')}</p></div>
                        <div className="guide-info">
                          <p>{t('guide.withdrawBinanceUnique')}</p>
                          <p>{t('guide.withdrawFee', { fee: withdrawInfo.feePercent })}</p>
                          <p>{t('guide.withdrawDelay', { n: withdrawInfo.perMonth })}</p>
                          <p>{t('guide.withdrawSkip')}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PARRAINAGE */}
                  <div className={`accordion-item ${expandedHelp === 'referral' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('referral')}>
                      <span className="accordion-icon">🤝</span>
                      <span className="accordion-title">{t('guide.referralProgram')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'referral' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'referral' && (
                      <div className="accordion-content guide-steps">
                        <h4>{t('guide.referralTitle')}</h4>
                        <div className="guide-step"><p>{t('guide.referralStep1')}</p></div>
                        <div className="guide-step"><p>{t('guide.referralStep2')}</p></div>
                        <div className="guide-step"><p>{t('guide.referralStep3')}</p></div>
                        <div className="guide-step"><p>{t('guide.referralStep4')}</p></div>
                        <div className="guide-step"><p>{t('guide.referralStep5')}</p></div>
                        <div className="guide-note">{t('guide.referralRanks')}</div>
                      </div>
                    )}
                  </div>

                  {/* RANGS */}
                  <div className={`accordion-item ${expandedHelp === 'ranks' ? 'expanded' : ''}`}>
                    <button className="accordion-header" onClick={() => toggleHelp('ranks')}>
                      <span className="accordion-icon">🏆</span>
                      <span className="accordion-title">{t('guide.ranksTitle')}</span>
                      <span className="accordion-arrow">{expandedHelp === 'ranks' ? '▼' : '▶'}</span>
                    </button>
                    {expandedHelp === 'ranks' && (
                      <div className="accordion-content">
                        <div className="ranks-list">
                          <div className="rank-item bronze">🥉 {t('guide.rankBronze')}</div>
                          <div className="rank-item silver">🥈 {t('guide.rankSilver')}</div>
                          <div className="rank-item gold">🥇 {t('guide.rankGold')}</div>
                          <div className="rank-item platinum">💎 {t('guide.rankPlatinum')}</div>
                          <div className="rank-item diamond">👑 {t('guide.rankDiamond')}</div>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* ── SUPPORT (messagerie intégrée) ── */}
              <div className="section-card">
                <h3>{t('guide.supportTitle')}</h3>
                <div className="contact-items">
                  <button className="contact-item" onClick={() => goToSection('support')}>
                    <span className="contact-icon">💬</span>
                    <span>{t('guide.supportContact')}</span>
                  </button>
                </div>
              </div>

              {/* ── AVERTISSEMENT ── */}
              <div className="section-card warning-card">
                <h3>{t('guide.warningTitle')}</h3>
                <div className="warning-content">
                  <div className="warning-item">
                    <span className="warning-icon">⚠️</span>
                    <div className="warning-text">
                      <p>{t('guide.warningRisk')}</p>
                    </div>
                  </div>
                  <div className="warning-item">
                    <span className="warning-icon">🆘</span>
                    <div className="warning-text">
                      <p>{t('guide.warningAddiction')}</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {activeSection === 'settings' && (
            <div className="settings-section">
              {/* Préférences */}
              <div className="settings-group">
                <h4 className="settings-group-title">{t('settings.preferences')}</h4>
                <div className="settings-list">
                  {/* Langue */}
                  <div className="settings-item expandable" onClick={() => setExpandedHelp(expandedHelp === 'lang' ? null : 'lang')}>
                    <div className="settings-item-left">
                      <div className="settings-icon-box">
                        <span className="settings-icon">🌍</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.language')}</span>
                        <span className="settings-item-value">
                          {availableLanguages.find(l => l.code === language)?.flag} {availableLanguages.find(l => l.code === language)?.name}
                        </span>
                      </div>
                    </div>
                    <span className={`settings-chevron ${expandedHelp === 'lang' ? 'open' : ''}`}>›</span>
                  </div>
                  {expandedHelp === 'lang' && (
                    <div className="settings-expand-content">
                      {availableLanguages.map(lang => (
                        <button
                          key={lang.code}
                          className={`lang-option ${language === lang.code ? 'active' : ''}`}
                          onClick={() => { handleLanguageChange(lang.code); setExpandedHelp(null); }}
                        >
                          <span className="lang-flag">{lang.flag}</span>
                          <span className="lang-name">{lang.name}</span>
                          {language === lang.code && <span className="lang-check">✓</span>}
                        </button>
                      ))}
                    </div>
                  )}

                </div>
              </div>

              {/* Sécurité */}
              <div className="settings-group">
                <h4 className="settings-group-title">{t('settings.security')}</h4>
                <div className="settings-list">
                  {/* Modifier mot de passe */}
                  <div className="settings-item expandable" onClick={() => setExpandedHelp(expandedHelp === 'password' ? null : 'password')}>
                    <div className="settings-item-left">
                      <div className="settings-icon-box security">
                        <span className="settings-icon">🔐</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.changePassword')}</span>
                        <span className="settings-item-desc">{t('settings.lastModified')}: {t('settings.never')}</span>
                      </div>
                    </div>
                    <span className={`settings-chevron ${expandedHelp === 'password' ? 'open' : ''}`}>›</span>
                  </div>
                  {expandedHelp === 'password' && (
                    <div className="settings-expand-content">
                      <form className="password-form" onSubmit={handlePasswordChange}>
                        <div className="form-group">
                          <label>{t('settings.currentPassword')}</label>
                          <div className="input-wrapper">
                            <input
                              type="password"
                              value={passwordForm.current}
                              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                              placeholder={t('settings.enterCurrentPassword')}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>{t('settings.newPassword')}</label>
                          <div className="input-wrapper">
                            <input
                              type="password"
                              value={passwordForm.new}
                              onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                              placeholder={t('settings.minChars')}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>{t('settings.confirmNewPassword')}</label>
                          <div className="input-wrapper">
                            <input
                              type="password"
                              value={passwordForm.confirm}
                              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                              placeholder={t('settings.retypePassword')}
                            />
                          </div>
                        </div>
                        {passwordError && <div className="form-error">⚠️ {passwordError}</div>}
                        {passwordSuccess && <div className="form-success">✓ {passwordSuccess}</div>}
                        <button type="submit" className="password-submit-btn">
                          {t('common.save')}
                        </button>
                      </form>
                    </div>
                  )}

                </div>
              </div>

              {/* Mon compte */}
              <div className="settings-group">
                <h4 className="settings-group-title">{t('settings.myAccount')}</h4>
                <div className="settings-list">
                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box account">
                        <span className="settings-icon">✉️</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.emailAddress')}</span>
                        <span className="settings-item-value">{user?.email}</span>
                      </div>
                    </div>
                    <span className="settings-badge verified">{t('common.verified')}</span>
                  </div>

                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box account">
                        <span className="settings-icon">📅</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('profile.memberSince')}</span>
                        <span className="settings-item-value">
                          {user?.createdAt ? new Date(user.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box account">
                        <span className="settings-icon">🆔</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.identifier')}</span>
                        <span className="settings-item-value mono">{user?.id?.slice(0, 8).toUpperCase()}</span>
                      </div>
                    </div>
                    <button className="settings-copy-btn" onClick={() => { navigator.clipboard.writeText(user?.id?.slice(0, 8).toUpperCase()); }}>
                      {t('common.copy')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="settings-group">
                <h4 className="settings-group-title">{t('settings.support')}</h4>
                <div className="settings-list">
                  <a href="mailto:support@cryptobot.com" className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box support">
                        <span className="settings-icon">💬</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.contactSupport')}</span>
                        <span className="settings-item-desc">support@cryptobot.com</span>
                      </div>
                    </div>
                    <span className="settings-chevron">›</span>
                  </a>

                  <a href="https://t.me/cryptobot_support" target="_blank" rel="noopener noreferrer" className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box telegram">
                        <span className="settings-icon">✈️</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.telegram')}</span>
                        <span className="settings-item-desc">{t('settings.joinCommunity')}</span>
                      </div>
                    </div>
                    <span className="settings-chevron">›</span>
                  </a>

                  <div className="settings-item" onClick={() => setActiveSection('help')}>
                    <div className="settings-item-left">
                      <div className="settings-icon-box help">
                        <span className="settings-icon">❓</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.helpCenter')}</span>
                        <span className="settings-item-desc">{t('settings.faqGuides')}</span>
                      </div>
                    </div>
                    <span className="settings-chevron">›</span>
                  </div>
                </div>
              </div>

              {/* À propos */}
              <div className="settings-group">
                <h4 className="settings-group-title">{t('settings.about')}</h4>
                <div className="settings-list">
                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon-box about">
                        <span className="settings-icon">📱</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.version')}</span>
                      </div>
                    </div>
                    <span className="settings-item-value mono">{appInfo?.app?.version || '1.0.0'}</span>
                  </div>

                  <div className="settings-item expandable" onClick={() => setExpandedHelp(expandedHelp === 'terms' ? null : 'terms')}>
                    <div className="settings-item-left">
                      <div className="settings-icon-box legal">
                        <span className="settings-icon">📄</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.termsOfService')}</span>
                      </div>
                    </div>
                    <span className={`settings-chevron ${expandedHelp === 'terms' ? 'open' : ''}`}>›</span>
                  </div>
                  {expandedHelp === 'terms' && (
                    <div className="settings-expand-content legal-content">
                      <h4>{t('legal.termsTitle')}</h4>

                      <div className="legal-section">
                        <h5>{t('legal.article1Title')}</h5>
                        <p>{t('legal.article1Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article2Title')}</h5>
                        <p>{t('legal.article2Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article3Title')}</h5>
                        <p>{t('legal.article3Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article4Title')}</h5>
                        <p>{t('legal.article4Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article5Title')}</h5>
                        <p>{t('legal.article5Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article6Title')}</h5>
                        <p>{t('legal.article6Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article7Title')}</h5>
                        <p>{t('legal.article7Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.article8Title')}</h5>
                        <p>{t('legal.article8Text')}</p>
                      </div>

                      <p className="legal-footer">{t('legal.lastUpdate')}</p>
                    </div>
                  )}

                  <div className="settings-item expandable" onClick={() => setExpandedHelp(expandedHelp === 'privacy' ? null : 'privacy')}>
                    <div className="settings-item-left">
                      <div className="settings-icon-box legal">
                        <span className="settings-icon">🔒</span>
                      </div>
                      <div className="settings-item-content">
                        <span className="settings-item-title">{t('settings.privacyPolicy')}</span>
                      </div>
                    </div>
                    <span className={`settings-chevron ${expandedHelp === 'privacy' ? 'open' : ''}`}>›</span>
                  </div>
                  {expandedHelp === 'privacy' && (
                    <div className="settings-expand-content legal-content">
                      <h4>{t('legal.privacyTitle')}</h4>

                      <div className="legal-section">
                        <h5>{t('legal.privacy1Title')}</h5>
                        <p>{t('legal.privacy1Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.privacy2Title')}</h5>
                        <p>{t('legal.privacy2Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.privacy3Title')}</h5>
                        <p>{t('legal.privacy3Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.privacy4Title')}</h5>
                        <p>{t('legal.privacy4Text')}</p>
                      </div>

                      <div className="legal-section">
                        <h5>{t('legal.privacy5Title')}</h5>
                        <p>{t('legal.privacy5Text')}</p>
                      </div>

                      <p className="legal-footer">{t('legal.privacyContact')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="settings-actions-compact">
                <button className="settings-link-btn logout">
                  {t('auth.logout')}
                </button>
                <span className="settings-divider">•</span>
                <button className="settings-link-btn delete">
                  {t('settings.deleteAccount')}
                </button>
              </div>

              {/* Footer */}
              <div className="settings-footer">
                <p>CryptoBot Trading © {new Date().getFullYear()}</p>
                <p className="settings-footer-sub">{t('common.allRightsReserved')}</p>
              </div>
            </div>
          )}

          {activeSection === 'support' && (
            <div className="support-section">
              {/* Header façon contact de messagerie */}
              <div className="support-header">
                <div className="support-avatar">
                  <span>🎧</span>
                  <span className="support-avatar-dot"></span>
                </div>
                <div className="support-title">
                  <h3>{t('support.helpCenter')}</h3>
                  <p className="support-subtitle">
                    <span className="support-online-text">{t('support.online')}</span>
                    <span className="support-subtitle-sep">·</span>
                    {t('support.replyTime')}
                  </p>
                </div>
              </div>

              {messageError && (
                <div className="support-error">
                  <span>⚠️</span> {messageError}
                </div>
              )}

              {/* Zone de conversation */}
              <div className="chat-container" ref={chatContainerRef}>
                {messagesLoading ? (
                  <div className="chat-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('support.loadingConversation')}</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-empty">
                    <div className="empty-icon">💬</div>
                    <h4>{t('support.noConversation')}</h4>
                    <p>{t('support.askFirstQuestion')}</p>
                  </div>
                ) : (
                  <div className="chat-messages">
                    {/* Alerte expiration si réponse admin */}
                    {messages.some(m => m.expiresAt) && (
                      <div className="expiration-notice">
                        <span>🕐</span>
                        {t('support.archivedOn')}{' '}
                        <strong>
                          {new Date(messages.find(m => m.expiresAt)?.expiresAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
                            day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                          })}
                        </strong>
                      </div>
                    )}

                    {messages.map((msg, index) => {
                      const prev = messages[index - 1];
                      const next = messages[index + 1];
                      const showDay = index === 0 || !isSameDay(msg.createdAt, prev.createdAt);
                      const groupedWithPrev = !showDay && areGrouped(prev, msg);
                      const isLastOfGroup = !areGrouped(msg, next);
                      const isUser = msg.senderRole === 'user';
                      return (
                        <React.Fragment key={msg.id}>
                          {showDay && (
                            <div className="chat-day-sep">
                              <span>{chatDayLabel(msg.createdAt, language)}</span>
                            </div>
                          )}
                          <div
                            className={[
                              'chat-msg',
                              isUser ? 'out' : 'in',
                              groupedWithPrev ? 'grouped' : 'group-start',
                              isLastOfGroup ? 'group-end' : ''
                            ].join(' ')}
                          >
                            <div className="chat-bubble">
                              <span className="chat-bubble-text">{renderRichText(msg.content)}</span>
                              {isLastOfGroup && (
                                <span className="chat-bubble-meta">
                                  <span className="chat-bubble-time">{chatTimeLabel(msg.createdAt, language)}</span>
                                  {isUser && (
                                    <span className={`chat-tick ${msg.readAt ? 'seen' : ''}`}>
                                      {msg.readAt || msg.respondedAt ? '✓✓' : '✓'}
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Zone de saisie */}
              <form className="chat-input-area" onSubmit={handleSendMessage}>
                {remainingMessages <= 0 ? (
                  <div className="input-disabled-notice">
                    <span>⏳</span>
                    <p>{t('support.messageLimit')}</p>
                  </div>
                ) : (
                  <>
                    <div className="input-wrapper">
                      <textarea
                        placeholder={t('support.writePlaceholder')}
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        maxLength={1000}
                        rows={1}
                        disabled={messageSending}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (messageInput.trim() && !messageSending) {
                              handleSendMessage(e);
                            }
                          }
                        }}
                      />
                      <div className="input-actions">
                        <span className="char-counter">{messageInput.length}/1000</span>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="send-btn"
                      disabled={!messageInput.trim() || messageSending}
                    >
                      {messageSending ? (
                        <span className="sending-spinner"></span>
                      ) : (
                        <span>➤</span>
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
